import React from 'react';
import { X, AlertTriangle, TrendingUp, CheckCircle } from 'lucide-react';

/**
 * Modal to preview and confirm applying reader cost recovery to all services
 */
export default function ApplyToAllServicesModal({
  isOpen,
  onClose,
  onConfirm,
  servicesToUpdate,
  rateIncrease,
  loading = false
}) {
  if (!isOpen) return null;

  const formatPercent = (value) => `${(value || 0).toFixed(2)}%`;
  const formatBasisPoints = (value) => `${Math.round((value || 0) * 100)} bps`;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <span className="text-2xl">⚡</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Apply to All Services</h2>
              <p className="text-sm text-purple-100">Preview changes before applying</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-white/80 hover:text-white transition-colors disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Summary Card */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 mb-6 border-2 border-blue-200">
            <div className="flex items-start gap-3">
              <TrendingUp className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="font-bold text-blue-900 mb-2">Rate Increase Summary</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-blue-600 uppercase tracking-wide mb-1">Rate Increase</p>
                    <p className="text-2xl font-bold text-blue-700">
                      +{formatBasisPoints(rateIncrease)}
                    </p>
                    <p className="text-sm text-blue-600">({formatPercent(rateIncrease)} points)</p>
                  </div>
                  <div>
                    <p className="text-xs text-blue-600 uppercase tracking-wide mb-1">Services Affected</p>
                    <p className="text-2xl font-bold text-blue-700">{servicesToUpdate.length}</p>
                    <p className="text-sm text-blue-600">with high-cost readers</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Warning Notice */}
          <div className="bg-amber-50 border-l-4 border-amber-400 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                <p className="font-semibold mb-1">This action will:</p>
                <ul className="list-disc ml-4 space-y-1">
                  <li>Increase rates for <strong>{servicesToUpdate.length} service(s)</strong> by <strong>+{formatBasisPoints(rateIncrease)}</strong></li>
                  <li>Automatically recalculate profitability for each service</li>
                  <li>Mark reader cost recovery as applied (cannot be undone)</li>
                  <li>Update all saved calculations in the proposal</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Services List */}
          <div className="mb-6">
            <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Services to Update ({servicesToUpdate.length})
            </h3>
            <div className="space-y-3">
              {servicesToUpdate.map((service, index) => {
                const currentRate = service.proposed_pricing?.paymentRates?.CC?.takeRate || 0;
                const newRate = currentRate + rateIncrease;
                
                return (
                  <div
                    key={service.id || index}
                    className="bg-white border-2 border-slate-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-slate-900 mb-1">
                          {service.service_name || 'Unnamed Service'}
                        </h4>
                        <p className="text-sm text-slate-600 mb-2">
                          {service.scenario_info?.readerType} × {service.scenario_info?.readersQty} readers
                        </p>
                        <div className="flex items-center gap-4 text-sm">
                          <div>
                            <span className="text-slate-500">Current CC Rate:</span>
                            <span className="ml-2 font-mono text-slate-700">{formatPercent(currentRate)}</span>
                          </div>
                          <div className="text-slate-400">→</div>
                          <div>
                            <span className="text-slate-500">New CC Rate:</span>
                            <span className="ml-2 font-mono font-bold text-green-700">{formatPercent(newRate)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                        +{formatBasisPoints(rateIncrease)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Info Note */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
            <p className="font-semibold mb-1">💡 Pro Tip:</p>
            <p>
              This distributes the reader hardware cost across all services proportionally. 
              Each service's rate increases by the same amount, ensuring fair cost recovery across your proposal.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-slate-50 px-6 py-4 flex items-center justify-between border-t border-slate-200">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-6 py-2 text-slate-600 hover:text-slate-900 font-medium transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Applying...
              </>
            ) : (
              <>
                <TrendingUp className="w-5 h-5" />
                Apply to {servicesToUpdate.length} Service{servicesToUpdate.length !== 1 ? 's' : ''}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
